import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { get, set, del } from 'idb-keyval';
import { OrderHashData, HashTable } from './common/api/orderHashMicroservice';

const STORAGE_KEY = 'medtrax_order_hash_store';

const mergeHashTables = (
  oldHashTable: HashTable,
  newHashTable: HashTable
): HashTable => {
  const merged: HashTable = { ...oldHashTable };

  for (const [key, newArray] of Object.entries(newHashTable)) {
    merged[key] = [...new Set([...(oldHashTable[key] ?? []), ...newArray])];
  }

  return merged;
};

const mergePrefixes = (
  oldPrefixes: string[],
  newPrefixes: string[]
): string[] => {
  return [...new Set([...oldPrefixes, ...newPrefixes])];
};

interface OrderHashStore {
  hashData: OrderHashData | null;
  hasHydrated: boolean;

  mergeHashData: (newData: OrderHashData | null) => void;
  clearHashData: () => void;
  setHasHydrated: (hydrated: boolean) => void;
}

export const useOrderHashStore = create<OrderHashStore>()(
  persist(
    set => ({
      hashData: null,
      hasHydrated: false,

      mergeHashData: (newData: OrderHashData | null) =>
        set(state => {
          const oldData = state.hashData;

          if (!oldData) {
            return {
              hashData: newData,
            };
          }

          return {
            hashData: {
              millisecondsSinceEpoch: newData.millisecondsSinceEpoch ?? 0,

              sgtinHashTable: mergeHashTables(
                oldData.sgtinHashTable ?? {},
                newData?.sgtinHashTable ?? {}
              ),

              sgtinHashTableSalted: mergeHashTables(
                oldData.sgtinHashTableSalted ?? {},
                newData?.sgtinHashTableSalted ?? {}
              ),

              sgtinHashTablePeppered: mergeHashTables(
                oldData.sgtinHashTablePeppered ?? {},
                newData?.sgtinHashTablePeppered ?? {}
              ),

              containerPrefixes: mergePrefixes(
                oldData.containerPrefixes ?? [],
                newData?.containerPrefixes ?? []
              )
            },
          };
        }),

      clearHashData: () =>
        set({
          hashData: null,
        }),

      setHasHydrated: (hydrated: boolean) =>
        set({
          hasHydrated: hydrated,
        }),
    }),
    {
      name: STORAGE_KEY,

      storage: createJSONStorage(() => ({
        getItem: async name => {
          const value = await get(name);
          return value ?? null;
        },

        setItem: async (name, value) => {
          await set(name, value);
        },

        removeItem: async name => {
          await del(name);
        },
      })),

      onRehydrateStorage: () => state => {
        state?.setHasHydrated(true);
      },
    }
  )
);
